import { createSlice } from '@reduxjs/toolkit';
import {
  fetchChaosActions,
  initChaosDefaults,
  syncChaosNewActions,
  updateChaosAction,
  testChaosAction,
  createChaosTwitchReward,
  bindChaosReward,
  fetchTwitchRewards,
  syncChaosTwitchRewards,
  fetchChaosClientStatus,
  generateChaosConnectToken,
  fetchChaosLogs
} from './chaos.actions';

const initialState = {
  actions: [],
  twitchRewards: [],
  syncReportByActionKey: {},
  clientStatus: {
    status: 'offline',
    deviceName: '',
    connectedAt: null,
    lastSeenAt: null
  },
  connectData: {
    clientId: null,
    connectToken: '',
    expiresInSec: null,
    wsUrl: ''
  },
  logs: [],
  isLoadingActions: false,
  isLoadingRewards: false,
  isSyncingTwitchRewards: false,
  isLoadingStatus: false,
  isLoadingConnectToken: false
};

const chaosSlice = createSlice({
  name: 'chaos',
  initialState,
  reducers: {
    clearChaosState: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(fetchChaosActions.pending, (state) => {
      state.isLoadingActions = true;
    });
    builder.addCase(fetchChaosActions.fulfilled, (state, action) => {
      state.actions = action.payload?.data || [];
      state.isLoadingActions = false;
    });
    builder.addCase(fetchChaosActions.rejected, (state) => {
      state.isLoadingActions = false;
    });

    builder.addCase(initChaosDefaults.pending, (state) => {
      state.isLoadingActions = true;
    });
    builder.addCase(initChaosDefaults.fulfilled, (state) => {
      state.isLoadingActions = false;
    });
    builder.addCase(initChaosDefaults.rejected, (state) => {
      state.isLoadingActions = false;
    });

    builder.addCase(syncChaosNewActions.pending, (state) => {
      state.isLoadingActions = true;
    });
    builder.addCase(syncChaosNewActions.fulfilled, (state) => {
      state.isLoadingActions = false;
    });
    builder.addCase(syncChaosNewActions.rejected, (state) => {
      state.isLoadingActions = false;
    });

    builder.addCase(updateChaosAction.fulfilled, (state, action) => {
      const updated = action.payload?.data;
      if (!updated?._id) return;
      const index = state.actions.findIndex((item) => String(item?._id) === String(updated._id));
      if (index >= 0) state.actions[index] = updated;
    });

    builder.addCase(testChaosAction.fulfilled, (state) => {
      state.isLoadingActions = false;
    });

    builder.addCase(createChaosTwitchReward.fulfilled, (state) => {
      state.isLoadingActions = false;
    });

    builder.addCase(bindChaosReward.fulfilled, (state) => {
      state.isLoadingActions = false;
    });

    builder.addCase(fetchTwitchRewards.pending, (state) => {
      state.isLoadingRewards = true;
    });
    builder.addCase(fetchTwitchRewards.fulfilled, (state, action) => {
      state.twitchRewards = action.payload?.data || [];
      state.isLoadingRewards = false;
    });
    builder.addCase(fetchTwitchRewards.rejected, (state) => {
      state.isLoadingRewards = false;
    });

    builder.addCase(syncChaosTwitchRewards.pending, (state) => {
      state.isLoadingRewards = true;
      state.isSyncingTwitchRewards = true;
      state.syncReportByActionKey = {};
    });
    builder.addCase(syncChaosTwitchRewards.fulfilled, (state, action) => {
      const report = action.payload?.data?.report || [];
      state.syncReportByActionKey = report.reduce((acc, item) => {
        if (item?.actionKey) {
          acc[item.actionKey] = item;
        }
        return acc;
      }, {});
      state.actions = action.payload?.data?.actions || state.actions;
      state.twitchRewards = action.payload?.data?.rewards || state.twitchRewards;
      state.isLoadingRewards = false;
      state.isSyncingTwitchRewards = false;
    });
    builder.addCase(syncChaosTwitchRewards.rejected, (state) => {
      state.isLoadingRewards = false;
      state.isSyncingTwitchRewards = false;
    });

    builder.addCase(fetchChaosClientStatus.pending, (state) => {
      state.isLoadingStatus = true;
    });
    builder.addCase(fetchChaosClientStatus.fulfilled, (state, action) => {
      state.clientStatus = action.payload?.data || initialState.clientStatus;
      state.isLoadingStatus = false;
    });
    builder.addCase(fetchChaosClientStatus.rejected, (state) => {
      state.isLoadingStatus = false;
    });

    builder.addCase(generateChaosConnectToken.pending, (state) => {
      state.isLoadingConnectToken = true;
    });
    builder.addCase(generateChaosConnectToken.fulfilled, (state, action) => {
      state.connectData = action.payload?.data || initialState.connectData;
      state.isLoadingConnectToken = false;
    });
    builder.addCase(generateChaosConnectToken.rejected, (state) => {
      state.isLoadingConnectToken = false;
    });

    builder.addCase(fetchChaosLogs.fulfilled, (state, action) => {
      state.logs = action.payload?.data || [];
    });
  }
});

export const { clearChaosState } = chaosSlice.actions;

export default chaosSlice.reducer;
